import * as THREE from 'three'
import { ColladaLoader, OBJLoader, MTLLoader } from 'three-stdlib'
import { FenceConfig, LoadedTextures } from './types'
import { loadTexturesForFence, applyTexturesToModel } from './textureLoader'
import { getFenceConfigWithColor } from '../../config/fenceConfigs'

    // 🚀 DEBUG FLAG - ustaw na true tylko do debugowania
const DEBUG_LOGS = true

/**
 * Cache dla załadowanych modeli
 */
const modelCache = new Map<string, THREE.Scene>()

/**
 * Cache dla Promise'ów ładowania modeli (deduplikacja równoległych żądań)
 * Jeśli wiele komponentów próbuje załadować ten sam model jednocześnie,
 * wszystkie czekają na ten sam Promise zamiast ładować model wielokrotnie
 */
const loadingPromises = new Map<string, Promise<THREE.Scene | null>>()

/**
 * Ładuje model DAE z teksturami
 */
export async function loadDAEModel(
  fenceData: FenceConfig,
  color?: string
): Promise<THREE.Scene | null> {
  try {
    // 🔄 Najpierw załaduj konfigurację, żeby uzyskać aktualną ścieżkę tekstury dla cache key
    const fenceConfig = await getFenceConfigWithColor(fenceData.id, color)
    if (!fenceConfig) {
      console.error('❌ loadDAEModel: Nie znaleziono konfiguracji dla:', fenceData.id)
      return null
    }

    // 🎯 Utwórz cache key który uwzględnia ścieżkę tekstury (żeby cache się invalidował przy zmianie konfiguracji)
    const simpleCacheKey = `${fenceData.id}_${color || 'default'}`
    
    // Dodaj hash ścieżki tekstury do cache key (jeśli istnieje colorTextures)
    let texturePathHash = ''
    if (fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]?.materialTexture) {
      const materialTexture = fenceConfig.colorTextures[color].materialTexture
      // Jeśli jest to tablica, pobierz nazwę z pierwszego pliku
      const firstPath = Array.isArray(materialTexture) ? materialTexture[0] : materialTexture
      const fileName = firstPath.substring(firstPath.lastIndexOf('/') + 1)
      texturePathHash = `_${fileName.substring(0, 30)}` // Ogranicz długość
      DEBUG_LOGS && console.log(`🔑 loadDAEModel: Cache key z teksturą: ${texturePathHash}`)
    }
    
    // Pełny cache key z hash grup + ścieżką tekstury
    const postGroups = fenceConfig?.elements?.post?.groups || []
    const sectionGroups = fenceConfig?.elements?.section?.groups || []
    const groupsHash = JSON.stringify([...postGroups, ...sectionGroups])
    const cacheKey = `${simpleCacheKey}${texturePathHash}_${groupsHash.substring(0, 100)}`
    
    // 🚀 Sprawdź cache z nowym kluczem
    // ⚠️ TYMCZASOWE: Wyłączony cache modeli dla Vision Bet Medium (debugowanie tekstur)
    if (fenceConfig.id !== 'vision-bet-medium') {
      const cachedModel = modelCache.get(cacheKey)
      if (cachedModel) {
        DEBUG_LOGS && console.log(`✅ loadDAEModel: Model z cache (${cacheKey})`)
        return cachedModel.clone()
      }
      
      // 🔄 Sprawdź czy model jest już w trakcie ładowania (deduplikacja równoległych żądań)
      const existingPromise = loadingPromises.get(cacheKey)
      if (existingPromise) {
        DEBUG_LOGS && console.log(`⏳ loadDAEModel: Model już w trakcie ładowania, czekam na istniejący Promise (${cacheKey})`)
        const result = await existingPromise
        return result ? result.clone() : null
      }
    }
    
    DEBUG_LOGS && console.log(`🔄 loadDAEModel: Model NIE jest w cache, ładuję nowy (key: ${cacheKey})`)
    
    DEBUG_LOGS && console.log(`📦 loadDAEModel START: fenceId=${fenceData.id}, color=${color || 'default'}`)

    DEBUG_LOGS && console.log(`📋 loadDAEModel: fenceConfig otrzymany - name=${fenceConfig.name}`)
    DEBUG_LOGS && console.log(`📋 loadDAEModel: fenceConfig.modelPath (główny)=${fenceConfig.modelPath}`)
    DEBUG_LOGS && console.log(`📋 loadDAEModel: fenceConfig.daeFile=${fenceConfig.daeFile}`)
    
    // Sprawdź czy kolor ma własny modelPath
    if (color && fenceConfig.colorTextures && fenceConfig.colorTextures[color]) {
      const colorConfig = fenceConfig.colorTextures[color]
      DEBUG_LOGS && console.log(`🎨 loadDAEModel: Sprawdzam colorTextures[${color}]:`, {
        hasModelPath: !!colorConfig.modelPath,
        modelPath: colorConfig.modelPath
      })
    }

    const modelPath = fenceConfig.daeFile || fenceConfig.modelPath
    if (!modelPath) {
      console.error('❌ loadDAEModel: Brak ścieżki do modelu DAE')
      return null
    }

    DEBUG_LOGS && console.log(`🎯 loadDAEModel: ŁADUJĘ MODEL DAE z ścieżki: ${modelPath}`)

    // 🔄 Utwórz Promise dla tego klucza (deduplikacja równoległych żądań)
    const loadPromise = (async () => {
      // Załaduj model
      DEBUG_LOGS && console.log(`🔄 loadDAEModel: Tworzę ColladaLoader`)
      const loader = new ColladaLoader()
    
    // 🔧 WYŁĄCZ automatyczne ładowanie tekstur - ustaw pusty resourcePath
    // ColladaLoader automatycznie ładuje tekstury z DAE, co powoduje ładowanie dużych JPG (38 MB)
    // Zamiast tego używamy tekstur z konfiguracji (np. webp 4.5 MB)
    loader.setResourcePath('') // Pusty path = brak automatycznego ładowania tekstur
    
    // 🔧 PRZECHWYĆ fetch, XMLHttpRequest i TextureLoader żeby zablokować ładowanie dużych JPG
    const originalFetch = window.fetch
    const originalXHROpen = XMLHttpRequest.prototype.open
    const originalTextureLoaderLoad = THREE.TextureLoader.prototype.load
    let blockedRequests = 0
    
    if (fenceConfig.id?.startsWith('vision-bet')) {
      // 1. Przechwyć fetch
      window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
        if (url.includes('vision-bet') && url.includes('material_1.jpg') && !url.includes('thumbnail')) {
          console.warn(`🚫 BLOKUJĘ fetch dla dużego JPG (38 MB): ${url}`)
          blockedRequests++
          return Promise.reject(new Error('Blocked: large JPG replaced with webp from config'))
        }
        return originalFetch.apply(this, arguments as any)
      }
      
      // 2. Przechwyć XMLHttpRequest
      XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
        const urlStr = typeof url === 'string' ? url : url.href
        if (urlStr.includes('vision-bet') && urlStr.includes('material_1.jpg') && !urlStr.includes('thumbnail')) {
          console.warn(`🚫 BLOKUJĘ XMLHttpRequest.open dla dużego JPG (38 MB): ${urlStr}`)
          blockedRequests++
          // Wywołaj oryginalną funkcję z nieprawidłowym URL żeby zablokować żądanie
          return originalXHROpen.apply(this, ['GET', 'about:blank', ...rest] as any)
        }
        return originalXHROpen.apply(this, arguments as any)
      }
      
      // 3. Przechwyć TextureLoader (na wypadek gdyby używał)
      THREE.TextureLoader.prototype.load = function(url: string, onLoad?: any, onProgress?: any, onError?: any) {
        if (url.includes('vision-bet') && url.includes('material_1.jpg') && !url.includes('thumbnail')) {
          console.warn(`🚫 BLOKUJĘ TextureLoader.load dla dużego JPG (38 MB): ${url}`)
          blockedRequests++
          if (onError) setTimeout(() => onError(new Error('Blocked: large JPG replaced with webp from config')), 0)
          return {} as THREE.Texture
        }
        return originalTextureLoaderLoad.apply(this, arguments as any)
      }
    }
    
    DEBUG_LOGS && console.log(`⏳ loadDAEModel: Ładuję model z: ${modelPath} (bez automatycznych tekstur)`)
    
    let collada
    try {
      collada = await loader.loadAsync(modelPath)
    } finally {
      // ZAWSZE przywróć oryginalne funkcje, nawet jeśli był błąd
      if (fenceConfig.id?.startsWith('vision-bet')) {
        window.fetch = originalFetch
        XMLHttpRequest.prototype.open = originalXHROpen
        THREE.TextureLoader.prototype.load = originalTextureLoaderLoad
        if (blockedRequests > 0) {
          console.log(`✅ Zablokowano ${blockedRequests} żądań dla dużych JPG - użyte zostaną webp z konfiguracji`)
        }
      }
    }
    
    const model = collada.scene
    DEBUG_LOGS && console.log(`✅ loadDAEModel: Model załadowany pomyślnie z: ${modelPath}`)

    // 🔧 Pusty resourcePath powinien zapobiec automatycznemu ładowaniu tekstur,
    // ale sprawdźmy czy przypadkiem coś się nie załadowało
    let foundAutoTextures = 0
    model.traverse((child: any) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material]
        materials.forEach((mat: any) => {
          if (mat.map && mat.map.image) {
            const textureUrl = mat.map.image?.src || mat.map.image?.currentSrc || 'unknown'
            if (textureUrl.includes('material_1.jpg')) {
              console.warn(`⚠️ UWAGA: ColladaLoader załadował JPG mimo pustego resourcePath! URL: ${textureUrl}`)
              console.warn(`⚠️ Usuwam tę teksturę i zastąpię naszą z konfiguracji...`)
              mat.map.dispose()
              mat.map = null
              foundAutoTextures++
            }
          }
        })
      }
    })
    if (foundAutoTextures > 0) {
      console.warn(`⚠️ Znaleziono i usunięto ${foundAutoTextures} automatycznie załadowanych tekstur JPG`)
    }

    // Załaduj i aplikuj tekstury z konfiguracji (nadpiszą automatycznie załadowane)
    const textures = await loadTexturesForFence(fenceData, color)
    DEBUG_LOGS && console.log(`🎨 loadDAEModel: Załadowane tekstury z konfiguracji:`, textures ? 'TAK' : 'NIE')
    if (textures) {
      applyTexturesToModel(model, textures, fenceConfig)
    }

    // Zapisz w cache
    modelCache.set(cacheKey, model)
    
    // Usuń Promise z mapy loadingPromises po zakończeniu
    loadingPromises.delete(cacheKey)

    return model
    })().catch((error) => {
      // Usuń Promise z mapy loadingPromises nawet w przypadku błędu
      loadingPromises.delete(cacheKey)
      console.error('Błąd ładowania modelu DAE:', error)
      return null
    })
    
    // Zapisz Promise w mapie loadingPromises (tylko jeśli nie ma w cache)
    if (fenceConfig.id !== 'vision-bet-medium') {
      loadingPromises.set(cacheKey, loadPromise)
    }
    
    const loadedModel = await loadPromise
    return loadedModel ? loadedModel.clone() : null
  } catch (error) {
    console.error('Błąd ładowania modelu DAE:', error)
    return null
  }
}

/**
 * Ładuje model domu z teksturą (obsługuje DAE i OBJ)
 */
export async function loadHouseModel(modelPath: string): Promise<THREE.Scene | null> {
  console.log(`🏠 loadHouseModel: START - ładuję model domu z: ${modelPath}`)
  try {
    const cacheKey = `house_${modelPath}`
    
    // Sprawdź cache
    if (modelCache.has(cacheKey)) {
      console.log(`🏠 loadHouseModel: Model z cache: ${modelPath}`)
      return modelCache.get(cacheKey)!.clone()
    }

    const resourcePath = modelPath.substring(0, modelPath.lastIndexOf('/')) + '/'
    const isOBJ = modelPath.toLowerCase().endsWith('.obj')
    
    let houseModel: THREE.Group | THREE.Scene
    
    if (isOBJ) {
      // Ładowanie OBJ z MTL
      console.log(`🏠 loadHouseModel: Ładuję OBJ z: ${modelPath}`)
      const mtlPath = modelPath.replace('.obj', '.mtl')
      
      try {
        // Najpierw załaduj materiały
        const mtlLoader = new MTLLoader()
        mtlLoader.setResourcePath(resourcePath)
        const materials = await mtlLoader.loadAsync(mtlPath)
        materials.preload()
        console.log(`🏠 loadHouseModel: MTL załadowany: ${mtlPath}`)
        
        // Potem załaduj OBJ z materiałami
        const objLoader = new OBJLoader()
        objLoader.setMaterials(materials)
        houseModel = await objLoader.loadAsync(modelPath)
        console.log(`🏠 loadHouseModel: OBJ załadowany: ${modelPath}`)
      } catch (mtlError) {
        console.warn(`⚠️ loadHouseModel: Błąd MTL, ładuję OBJ bez materiałów:`, mtlError)
        const objLoader = new OBJLoader()
        houseModel = await objLoader.loadAsync(modelPath)
      }
      
      // Konfiguracja modelu OBJ - powiększenie 45x i ustawienie na ziemi
      houseModel.scale.set(0.45, 0.45, 0.45) // 45x większy (0.01 * 45 = 0.45)
      
      // Oblicz bounding box i ustaw dom na ziemi (y=0)
      const box = new THREE.Box3().setFromObject(houseModel)
      const minY = box.min.y
      houseModel.position.set(0, -minY, -7) // Przesuń w górę o wartość najniższego punktu, Z=-7m
      houseModel.rotation.y = Math.PI // Obrót o 180 stopni wokół osi Y
      console.log(`🏠 loadHouseModel: Bounding box Y: min=${minY.toFixed(3)}, przesunięcie do y=${-minY.toFixed(3)}, rotacja: 180°`)
      
    } else {
      // Ładowanie DAE (COLLADA)
      const loader = new ColladaLoader()
      loader.setResourcePath(resourcePath)
      
      console.log(`🏠 loadHouseModel: Ładuję DAE z: ${modelPath}`)
      const colladaResult = await loader.loadAsync(modelPath)
      houseModel = colladaResult.scene
      
      // Konfiguracja modelu DAE
      houseModel.position.set(0, 0, -5)
      houseModel.scale.set(0.5, 0.5, 0.5)

      // Załaduj i aplikuj teksturę domu
      const textureLoader = new THREE.TextureLoader()
      const houseTexturePath = '/Cottage_Clean_Base_Color.png'
      
      try {
        console.log(`🎨 loadHouseModel: Ładuję teksturę domu z: ${houseTexturePath}`)
        const response = await fetch(houseTexturePath)
        if (response.ok) {
          const blob = await response.blob()
          const objectUrl = URL.createObjectURL(blob)
          const houseTexture = await textureLoader.loadAsync(objectUrl)
          URL.revokeObjectURL(objectUrl)
          
          // Konfiguracja tekstury
          houseTexture.wrapS = THREE.RepeatWrapping
          houseTexture.wrapT = THREE.RepeatWrapping
          houseTexture.generateMipmaps = true
          houseTexture.minFilter = THREE.LinearMipmapLinearFilter
          houseTexture.magFilter = THREE.LinearFilter
          houseTexture.anisotropy = 16
          houseTexture.colorSpace = THREE.SRGBColorSpace
          houseTexture.needsUpdate = true
          
          // Aplikuj teksturę do wszystkich meshów w modelu domu
          houseModel.traverse((child: any) => {
            if (child.isMesh && child.material) {
              child.material = new THREE.MeshStandardMaterial({
                map: houseTexture,
                roughness: 0.8,
                metalness: 0.1,
                envMapIntensity: 1.0,
                side: THREE.DoubleSide
              })
              console.log(`✅ loadHouseModel: Zastosowano teksturę do mesh: ${child.name}`)
            }
          })
          
          console.log(`✅ loadHouseModel: Tekstura domu załadowana pomyślnie`)
        } else {
          console.warn(`⚠️ loadHouseModel: Nie znaleziono tekstury domu (${response.status}), używam domyślnego materiału`)
        }
      } catch (textureError) {
        console.warn('⚠️ loadHouseModel: Błąd ładowania tekstury domu:', textureError)
      }
    }
    
    console.log(`🏠 loadHouseModel: Model załadowany pomyślnie: ${modelPath}`)

    // Zapisz w cache
    const sceneWrapper = new THREE.Scene()
    sceneWrapper.add(houseModel)
    modelCache.set(cacheKey, sceneWrapper)

    return sceneWrapper.clone()
  } catch (error) {
    console.error('🏠 Błąd ładowania modelu domu:', error)
    return null
  }
}

/**
 * Ładuje dowolny model DAE po ścieżce (bez nakładania tekstur ani specjalnych pozycji)
 */
export async function loadDAESceneByPath(modelPath: string): Promise<THREE.Scene | null> {
  try {
    const cacheKey = `path_${modelPath}`
    if (modelCache.has(cacheKey)) {
      return modelCache.get(cacheKey)!.clone()
    }

    const loader = new ColladaLoader()
    const resourcePath = modelPath.substring(0, modelPath.lastIndexOf('/')) + '/'
    loader.setResourcePath(resourcePath)
    let scene: THREE.Scene | null = null
    try {
      const collada = await loader.loadAsync(modelPath)
      scene = collada.scene
    } catch (primaryError) {
      console.warn('ColladaLoader loadAsync failed, attempting sanitize+parse fallback:', primaryError)
      // Fallback: pobierz jako tekst, usuń/napraw typowe błędy XML i sparsuj
      const resp = await fetch(modelPath, { cache: 'no-cache' })
      if (!resp.ok) throw new Error(`HTTP ${resp.status} while fetching ${modelPath}`)
      let xmlText = await resp.text()
      // Usuń BOM jeśli występuje
      if (xmlText.charCodeAt(0) === 0xFEFF) {
        xmlText = xmlText.slice(1)
      }
      // Usuń atrybuty crossorigin (bez wartości lub puste)
      xmlText = xmlText.replace(/\s+crossorigin(=(["']).*?\2)?/gi, '')
      // Napraw niezakodowane ampersandy (poza już poprawnymi encjami)
      xmlText = xmlText.replace(/&(?![a-zA-Z]+;|#\d+;|#x[0-9a-fA-F]+;)/g, '&amp;')
      const parsed = loader.parse(xmlText, resourcePath)
      scene = parsed.scene
    }

    modelCache.set(cacheKey, scene)
    return scene.clone()
  } catch (error) {
    console.error('Błąd ładowania modelu DAE po ścieżce:', error)
    return null
  }
}

/**
 * Czyści cache modeli
 */
export function clearModelCache(): void {
  modelCache.clear()
  loadingPromises.clear()
}

/**
 * Klonuje element z modelu DAE
 */
export function cloneElementFromModel(
  model: THREE.Scene,
  elementName: string
): THREE.Object3D | null {
  const sketchUpGroup = model.children.find((child: any) => child.name === 'SketchUp')
  if (!sketchUpGroup) return null

  const element = sketchUpGroup.children.find((child: any) => child.name === elementName)
  return element ? element.clone() : null
}

/**
 * Przygotowuje element do dodania do sceny (rotacja, skala, tekstury)
 */
export function prepareElement(
  element: THREE.Object3D,
  config: {
    modelScale: number
    positionY: number
    elementId: string
    textures?: LoadedTextures
    elementWidth?: number // Szerokość dla pivot point
    fenceConfig?: any // Konfiguracja ogrodzenia dla tekstur
  }
): void {
  // KLUCZOWE: Przesuń model lokalnie w prawo o połowę szerokości
  // Grupa jest w środku elementu, ale chcemy rotować wokół LEWEJ krawędzi
  // Więc model musi być przesunięty tak, żeby jego LEWA krawędź była w pozycji grupy - width/2
  // Model (który ma środek w 0,0,0) przesunięty o +width/2 będzie miał:
  // - lewą krawędź w 0
  // - środek w +width/2
  // Więc lewa krawędź będzie w pozycji grupy - width/2 (czyli w lewej krawędzi elementu)
  const offsetX = config.elementWidth ? config.elementWidth / 2 : 0
  
  // Ustaw pozycję (z offsetem X dla pivot point)
  element.position.set(offsetX, config.positionY, 0)

  // Obróć element (modele DAE są poziome, obracamy na pion)
  element.rotation.set(Math.PI / 2, Math.PI, 0)

  // Zastosuj skalę
  element.scale.setScalar(config.modelScale)

  // Dodaj ID do userData (dla raycastingu)
  element.userData.fenceElementId = config.elementId

  // 🚀 OPTYMALIZACJA: Zawsze aplikuj tekstury, ale z losowym offsetem dla każdego elementu
  // To zapewnia że każdy element wygląda inaczej (losowy offset tekstury)
  // Ale tekstury są już w cache, więc nie ładujemy ich ponownie - tylko aplikujemy z nowym offsetem
  DEBUG_LOGS && console.log(`🔍 DEBUG: prepareElement - config.textures:`, config.textures)
  if (config.textures) {
    DEBUG_LOGS && console.log(`🔍 DEBUG: prepareElement - wywołuję applyTexturesToModel (z losowym offsetem dla unikalności)`)
    applyTexturesToModel(element, config.textures, config.fenceConfig)
  } else {
    DEBUG_LOGS && console.log(`🔍 DEBUG: prepareElement - brak tekstur, nie wywołuję applyTexturesToModel`)
  }
}

/**
 * 🚀 PRELOADING: Załaduj model i tekstury w tle PRZED dodaniem elementu
 * Wywołaj tę funkcję gdy użytkownik wybierze system ogrodzenia
 */
export async function preloadFenceAssets(
  fenceId: string,
  color?: string
): Promise<boolean> {
  try {
    // ⚠️ Nie ładuj dla koloru "default" jeśli ogrodzenie wymaga konkretnego koloru (np. Vision Bet)
    // Sprawdź czy kolor istnieje w colorTextures
    const fenceConfig = await getFenceConfigWithColor(fenceId, color)
    if (!fenceConfig) {
      console.error('❌ PRELOAD: Nie znaleziono konfiguracji dla:', fenceId)
      return false
    }
    
    // Jeśli nie podano koloru, ale ogrodzenie ma colorTextures, nie ładuj (będzie błąd)
    if (!color && fenceConfig.colorTextures && Object.keys(fenceConfig.colorTextures).length > 0) {
      DEBUG_LOGS && console.log(`⏭️ PRELOAD: Pomijam preload dla ${fenceId} bez koloru (wymaga wyboru koloru)`)
      return false
    }
    
    DEBUG_LOGS && console.log(`🚀 PRELOAD START: Ładuję w tle model i tekstury dla ${fenceId}, kolor: ${color || 'default'}`)
    
    // Załaduj model (trafi do cache, deduplikacja równoległych żądań jest już w loadDAEModel)
    const model = await loadDAEModel(fenceConfig, color)
    if (!model) {
      console.error('❌ PRELOAD: Nie udało się załadować modelu dla:', fenceId)
      return false
    }
    
    DEBUG_LOGS && console.log(`✅ PRELOAD COMPLETE: Model i tekstury dla ${fenceId} załadowane do cache`)
    return true
  } catch (error) {
    console.error('❌ PRELOAD ERROR:', error)
    return false
  }
}

/**
 * Sprawdza czy model jest już w cache
 */
export function isModelCached(fenceId: string, color?: string): boolean {
  // Uproszczona wersja - sprawdza czy jakikolwiek model z tym ID jest w cache
  for (const key of modelCache.keys()) {
    if (key.startsWith(`${fenceId}_${color || 'default'}`)) {
      return true
    }
  }
  return false
}

